import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  Ban,
  Boxes,
  Check,
  CheckCircle2,
  Copy,
  ImageOff,
  Loader2,
  MailCheck,
  MapPin,
  PackageCheck,
  PackagePlus,
  Plane,
  Scale,
  TriangleAlert,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EnumSelect } from '@/components/ui/enum-select'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ApiClientError, apiClient } from '@/lib/api/client'
import type {
  ParcelAddressInfo,
  ParcelPlatform,
  ParcelWarehouseAddress,
  WarehouseParcel,
  WarehouseParcelStatus,
  WarehouseRateCard,
} from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { ErrorState } from '@/src/components/ErrorState'
import { ParcelTrackingPanel } from '@/src/components/tracking/ParcelTrackingPanel'
import { LoadingState } from '@/src/components/LoadingState'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import {
  buildPreAlert,
  EMPTY_PRE_ALERT,
  formatKg,
  formatMoney,
  formatShipWeek,
  PARCEL_PLATFORMS,
  PARCEL_STATUS,
  parcelErrorMessage,
  PLATFORM_LABELS,
  type PreAlertErrors,
  type PreAlertForm,
} from '@/src/lib/parcels'

/**
 * A customer's parcels to the China warehouse: their own address to paste
 * into Taobao, JD, 1688 or Pinduoduo, the price and rules, telling us what is
 * coming, and following it. Most customers use it on a phone.
 */

const STATUS_ICONS: Record<WarehouseParcelStatus, LucideIcon> = {
  EXPECTED: Truck,
  RECEIVED: PackageCheck,
  CONSOLIDATED: Plane,
  CANCELLED: Ban,
}

const PLATFORM_OPTIONS = PARCEL_PLATFORMS.map((value) => ({
  value,
  label: PLATFORM_LABELS[value],
}))

/** Card padding: tighter on a phone, where the shell already pads the page. */
const PAD = 'px-4 sm:px-6'

export function ParcelsPage() {
  const address = useAsyncData(() => apiClient.getParcelAddress(), [])
  const parcels = useAsyncData(() => apiClient.listMyParcels(), [])
  const [issued, setIssued] = useState<ParcelAddressInfo | null>(null)
  // Changes made here since the list last loaded; a reload starts afresh.
  const [changed, setChanged] = useState<{
    base: WarehouseParcel[] | null
    list: WarehouseParcel[]
  } | null>(null)

  const info = issued ?? address.data
  const list = changed && changed.base === parcels.data ? changed.list : parcels.data
  const change = (update: (current: WarehouseParcel[]) => WarehouseParcel[]) =>
    setChanged({ base: parcels.data, list: update(list ?? []) })
  const replace = (parcel: WarehouseParcel) =>
    change((current) => current.map((item) => (item.id === parcel.id ? parcel : item)))

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <PageHeader
        title="My parcels"
        description="Shop on Taobao, JD, 1688 or Pinduoduo, or send goods you already have, to our warehouse in Guangzhou. We fly them to Africa."
      />

      <Card className="gap-4">
        <CardHeader className={PAD}>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5" aria-hidden />
            Your warehouse address
          </CardTitle>
          <CardDescription>
            Paste it into Taobao, JD, 1688 or Pinduoduo as your delivery address. Your parcels go to
            our warehouse in Guangzhou, and we fly them to you.
          </CardDescription>
        </CardHeader>
        <CardContent className={PAD}>
          {address.loading && !info ? (
            <LoadingState label="Loading your address" />
          ) : address.error && !info ? (
            <ErrorState error={parcelErrorMessage(address.error)} onRetry={address.reload} />
          ) : !info ? null : !info.email_confirmed ? (
            <ConfirmEmailFirst />
          ) : !info.address ? (
            <IssueAddress onIssued={setIssued} onEmailUnconfirmed={address.reload} />
          ) : (
            <AddressDetails address={info.address} />
          )}
        </CardContent>
      </Card>

      {info && <PriceAndRules rates={info.rates} rules={info.rules} />}

      {info?.email_confirmed && info.address && (
        <PreAlertCard
          question={info.rules.declaration_question}
          onCreated={(parcel) =>
            parcels.data ? change((current) => [parcel, ...current]) : parcels.reload()
          }
        />
      )}

      <Card className="gap-4">
        <CardHeader className={PAD}>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Boxes className="h-5 w-5" aria-hidden />
            Your parcels
          </CardTitle>
        </CardHeader>
        <CardContent className={PAD}>
          {parcels.loading && !list ? (
            <LoadingState label="Loading your parcels" />
          ) : parcels.error && !list ? (
            <ErrorState error={parcelErrorMessage(parcels.error)} onRetry={parcels.reload} />
          ) : !list?.length ? (
            <div className="flex flex-col items-center rounded-lg border border-dashed px-4 py-10 text-center">
              <Boxes className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="font-medium">No parcels yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                When you send something to your warehouse address, tell us it&apos;s coming and
                follow it here.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" aria-label="Your parcels">
              {list.map((parcel) => (
                <ParcelItem
                  key={parcel.id}
                  parcel={parcel}
                  currency={info?.rates.currency ?? 'USD'}
                  onChange={replace}
                  onStale={parcels.reload}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ConfirmEmailFirst() {
  return (
    <div className="flex gap-3 rounded-lg border border-dashed p-4 text-sm">
      <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
      <p>
        Your address appears here once you confirm your email address. Use the button at the top of
        the page to get the confirmation link, then open it from your inbox.
      </p>
    </div>
  )
}

function IssueAddress({
  onIssued,
  onEmailUnconfirmed,
}: {
  onIssued: (info: ParcelAddressInfo) => void
  onEmailUnconfirmed: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const issue = async () => {
    setBusy(true)
    setError(null)
    try {
      onIssued(await apiClient.issueParcelAddress())
    } catch (cause) {
      if (cause instanceof ApiClientError && cause.code === 'EMAIL_NOT_CONFIRMED') {
        onEmailUnconfirmed()
        return
      }
      setError(parcelErrorMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Your address carries your own customer code, so we know which parcels are yours.
      </p>
      <Button className="h-11 w-full sm:w-auto" onClick={issue} disabled={busy}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Get my warehouse address
      </Button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}

const ADDRESS_FIELDS: Array<{
  key: keyof ParcelWarehouseAddress
  chinese: string
  english: string
}> = [
  { key: 'recipient', chinese: '收货人', english: 'Recipient' },
  { key: 'phone', chinese: '手机号', english: 'Phone' },
  { key: 'region', chinese: '所在地区', english: 'Region' },
  { key: 'street_address', chinese: '详细地址', english: 'Street address' },
]

function AddressDetails({ address }: { address: ParcelWarehouseAddress }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <p className="text-sm text-muted-foreground">Your code</p>
        <p className="mt-1 font-mono text-3xl font-bold tracking-wider">{address.customer_code}</p>
        <p className="mt-2 text-sm">
          It&apos;s already in the name and address; keep it there so we can match your parcels.
        </p>
      </div>

      <dl className="divide-y rounded-lg border">
        {ADDRESS_FIELDS.map((field) => (
          <div key={field.key} className="flex items-center gap-3 p-3">
            <div className="min-w-0 flex-1">
              <dt className="text-xs text-muted-foreground">
                <span lang="zh-CN">{field.chinese}</span> {field.english}
              </dt>
              <dd lang="zh-CN" className="mt-1 font-medium break-words select-all">
                {address[field.key]}
              </dd>
            </div>
            <CopyButton value={address[field.key]} label={field.english.toLowerCase()} />
          </div>
        ))}
      </dl>

      <CopyButton value={address.full_address} label="full address" className="h-11 w-full">
        Copy full address
      </CopyButton>
    </div>
  )
}

function CopyButton({
  value,
  label,
  children,
  className,
}: {
  value: string
  /** What is copied, for screen readers: "recipient", "full address". */
  label: string
  children?: ReactNode
  className?: string
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setState('copied')
    } catch {
      setState('failed')
    }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setState('idle'), 2000)
  }

  return (
    <Button
      type="button"
      variant={children ? 'default' : 'outline'}
      onClick={copy}
      aria-label={children ? undefined : `Copy ${label}`}
      className={cn('h-10 shrink-0', className)}
    >
      {state === 'copied' ? <Check /> : <Copy />}
      <span>
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : (children ?? 'Copy')}
      </span>
      <span className="sr-only" aria-live="polite">
        {state === 'copied'
          ? `${label} copied`
          : state === 'failed'
            ? `Could not copy ${label}`
            : ''}
      </span>
    </Button>
  )
}

function PriceAndRules({
  rates,
  rules,
}: {
  rates: WarehouseRateCard
  rules: ParcelAddressInfo['rules']
}) {
  const prices = [
    {
      label: 'General cargo',
      value: `${formatMoney(rates.general_per_kg, rates.currency)} per kg`,
    },
    {
      label: 'Dangerous goods',
      value: `${formatMoney(rates.dangerous_per_kg, rates.currency)} per kg`,
    },
    { label: 'Minimum charge', value: formatKg(rates.minimum_chargeable_kg) },
  ]

  return (
    <Card className="gap-4">
      <CardHeader className={PAD}>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Scale className="h-5 w-5" aria-hidden />
          Price and rules
        </CardTitle>
        <CardDescription>
          We weigh and measure each parcel at the warehouse and charge by the kilo.{' '}
          {rules.charging_note ??
            'Bulky, light parcels are charged by volume weight (length × width × height in cm ÷ 6000).'}
        </CardDescription>
      </CardHeader>
      <CardContent className={cn('space-y-4', PAD)}>
        <dl className="divide-y rounded-lg border" aria-label="Prices">
          {prices.map((price) => (
            <div key={price.label} className="flex items-baseline justify-between gap-4 p-3">
              <dt>{price.label}</dt>
              <dd className="text-right font-semibold">{price.value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex gap-3 rounded-lg bg-muted p-3 text-sm">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-waiting" aria-hidden />
          <div className="space-y-1">
            <p className="font-medium">
              Not allowed in general cargo: {rules.prohibited_in_general_cargo.join(', ')}
            </p>
            <p className="text-muted-foreground">{rules.dangerous_goods_note}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function PreAlertCard({
  question,
  onCreated,
}: {
  question: string
  onCreated: (parcel: WarehouseParcel) => void
}) {
  const [form, setForm] = useState<PreAlertForm>(EMPTY_PRE_ALERT)
  const [errors, setErrors] = useState<PreAlertErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [added, setAdded] = useState<string | null>(null)

  const set = <K extends keyof PreAlertForm>(field: K, value: PreAlertForm[K]) => {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
    setAdded(null)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setFormError(null)
    const built = buildPreAlert(form)
    if (!built.ok) {
      setErrors(built.errors)
      return
    }
    setBusy(true)
    try {
      const parcel = await apiClient.preAlertParcel(built.parcel)
      onCreated(parcel)
      setForm(EMPTY_PRE_ALERT)
      setErrors({})
      setAdded(parcel.description)
    } catch (cause) {
      if (cause instanceof ApiClientError && cause.code === 'TRACKING_IN_USE') {
        setErrors({ trackingNumber: cause.message })
      } else {
        setFormError(parcelErrorMessage(cause))
      }
    } finally {
      setBusy(false)
    }
  }

  const describedBy = (id: string, hint = false) =>
    errors[id as keyof PreAlertErrors] || hint ? `${id}-note` : undefined

  return (
    <Card className="gap-4">
      <CardHeader className={PAD}>
        <CardTitle className="flex items-center gap-2 text-lg">
          <PackagePlus className="h-5 w-5" aria-hidden />
          Tell us what&apos;s coming
        </CardTitle>
        <CardDescription>
          Tell us about each order you send to the warehouse, so we can match it when it arrives.
        </CardDescription>
      </CardHeader>
      <CardContent className={PAD}>
        <form onSubmit={submit} noValidate className="space-y-5" aria-label="Tell us what's coming">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field data-invalid={Boolean(errors.trackingNumber)}>
              <FieldLabel htmlFor="trackingNumber">Tracking number</FieldLabel>
              <Input
                id="trackingNumber"
                value={form.trackingNumber}
                onChange={(event) => set('trackingNumber', event.target.value)}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={60}
                aria-invalid={Boolean(errors.trackingNumber)}
                aria-describedby={describedBy('trackingNumber', true)}
                className="h-11"
              />
              {errors.trackingNumber ? (
                <FieldError id="trackingNumber-note">{errors.trackingNumber}</FieldError>
              ) : (
                <FieldDescription id="trackingNumber-note">
                  Add it later if the seller hasn&apos;t shipped yet
                </FieldDescription>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="platform">Platform</FieldLabel>
              <EnumSelect
                value={form.platform}
                onValueChange={(value) => set('platform', value as ParcelPlatform)}
                options={PLATFORM_OPTIONS}
                placeholder="Where did you buy it?"
                triggerProps={{ id: 'platform', className: 'data-[size=default]:h-11' }}
              />
            </Field>

            <Field data-invalid={Boolean(errors.orderNumber)}>
              <FieldLabel htmlFor="orderNumber">Order number</FieldLabel>
              <Input
                id="orderNumber"
                value={form.orderNumber}
                onChange={(event) => set('orderNumber', event.target.value)}
                autoComplete="off"
                spellCheck={false}
                maxLength={80}
                aria-invalid={Boolean(errors.orderNumber)}
                aria-describedby={describedBy('orderNumber')}
                className="h-11"
              />
              <FieldError id="orderNumber-note">{errors.orderNumber}</FieldError>
            </Field>
          </div>

          <Field data-invalid={Boolean(errors.description)}>
            <FieldLabel htmlFor="description" required>
              What&apos;s inside
            </FieldLabel>
            <Textarea
              id="description"
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
              placeholder="For example: 2 pairs of trainers and a phone case"
              maxLength={500}
              rows={3}
              aria-required
              aria-invalid={Boolean(errors.description)}
              aria-describedby={describedBy('description')}
            />
            <FieldError id="description-note">{errors.description}</FieldError>
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field data-invalid={Boolean(errors.quantity)}>
              <FieldLabel htmlFor="quantity">Quantity</FieldLabel>
              <Input
                id="quantity"
                value={form.quantity}
                onChange={(event) => set('quantity', event.target.value)}
                inputMode="numeric"
                autoComplete="off"
                aria-invalid={Boolean(errors.quantity)}
                aria-describedby={describedBy('quantity')}
                className="h-11"
              />
              <FieldError id="quantity-note">{errors.quantity}</FieldError>
            </Field>

            <Field data-invalid={Boolean(errors.declaredValue)}>
              <FieldLabel htmlFor="declaredValue">Value in USD</FieldLabel>
              <Input
                id="declaredValue"
                value={form.declaredValue}
                onChange={(event) => set('declaredValue', event.target.value)}
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                aria-invalid={Boolean(errors.declaredValue)}
                aria-describedby={describedBy('declaredValue')}
                className="h-11"
              />
              <FieldError id="declaredValue-note">{errors.declaredValue}</FieldError>
            </Field>
          </div>

          <FieldSet aria-describedby="dangerous-note" className="gap-2">
            <FieldLegend variant="label">
              {question}
              <span aria-hidden="true" className="text-destructive">
                {' '}
                *
              </span>
            </FieldLegend>
            <div className="grid grid-cols-2 gap-3">
              {(['yes', 'no'] as const).map((answer) => (
                <label
                  key={answer}
                  className={cn(
                    'flex h-12 cursor-pointer items-center gap-3 rounded-md border px-4 text-sm font-medium transition-colors',
                    'has-checked:border-primary has-checked:bg-primary/5 has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50',
                    errors.dangerous && 'border-destructive',
                  )}
                >
                  <input
                    type="radio"
                    name="declared-dangerous"
                    value={answer}
                    checked={form.dangerous === answer}
                    onChange={() => set('dangerous', answer)}
                    required
                    className="size-4 accent-primary"
                  />
                  {answer === 'yes' ? 'Yes' : 'No'}
                </label>
              ))}
            </div>
            {errors.dangerous ? (
              <FieldError id="dangerous-note">{errors.dangerous}</FieldError>
            ) : (
              <FieldDescription id="dangerous-note">
                If yes, it is charged at the dangerous goods rate.
              </FieldDescription>
            )}
          </FieldSet>

          {formError && (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          )}

          <div className="space-y-3">
            <Button type="submit" className="h-11 w-full sm:w-auto" disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Add this parcel
            </Button>
            {added && (
              <p role="status" className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-done" aria-hidden />
                <span>
                  Added &ldquo;{added}&rdquo;. We&apos;ll look out for it at the warehouse.
                </span>
              </p>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

interface ParcelItemProps {
  parcel: WarehouseParcel
  currency: string
  onChange: (parcel: WarehouseParcel) => void
  /** The parcel changed at the warehouse since the list loaded. */
  onStale: () => void
}

function ParcelItem({ parcel, currency, onChange, onStale }: ParcelItemProps) {
  const editable = parcel.status === 'EXPECTED'
  const weighed = parcel.status === 'RECEIVED' || parcel.status === 'CONSOLIDATED'
  const status = PARCEL_STATUS[parcel.status]
  const StatusIcon = STATUS_ICONS[parcel.status]
  const bought = [
    parcel.platform && (PLATFORM_LABELS[parcel.platform as ParcelPlatform] ?? parcel.platform),
    parcel.order_number && `Order ${parcel.order_number}`,
  ].filter(Boolean)

  return (
    <li
      aria-labelledby={`parcel-${parcel.id}`}
      className={cn(
        'space-y-3 rounded-lg border p-4',
        parcel.status === 'CANCELLED' && 'bg-muted/40',
      )}
    >
      <div className="space-y-2">
        <p
          id={`parcel-${parcel.id}`}
          className={cn(
            'font-medium break-words',
            parcel.status === 'CANCELLED' && 'text-muted-foreground',
          )}
        >
          {parcel.description}
        </p>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={cn('py-1', status.tone)}>
            <StatusIcon aria-hidden />
            {status.label}
          </Badge>
          {parcel.declared_dangerous && (
            <Badge variant="accent-soft" className="py-1">
              <TriangleAlert aria-hidden />
              Declared dangerous goods
            </Badge>
          )}
        </div>
      </div>

      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        {bought.length > 0 && (
          <div className="min-w-0">
            <dt className="text-muted-foreground">Bought on</dt>
            <dd className="break-words">{bought.join(' · ')}</dd>
          </div>
        )}
        <div className="min-w-0">
          <dt className="text-muted-foreground">Tracking number</dt>
          <dd className="font-mono break-all">
            {parcel.tracking_number ??
              (editable ? (
                <AddTrackingNumber parcel={parcel} onChange={onChange} onStale={onStale} />
              ) : (
                <span className="font-sans text-muted-foreground">None</span>
              ))}
          </dd>
        </div>
      </dl>

      {parcel.tracking && parcel.status !== 'CANCELLED' && (
        <ParcelTrackingPanel tracking={parcel.tracking} title={parcel.description} />
      )}

      {weighed && (
        <div className="space-y-2 rounded-lg bg-muted/60 p-3 text-sm">
          <dl className="grid gap-2 sm:grid-cols-3 sm:gap-3">
            {[
              { label: 'Weight', value: parcel.weight_kg != null && formatKg(parcel.weight_kg) },
              {
                label: 'Chargeable weight',
                value: parcel.chargeable_kg != null && formatKg(parcel.chargeable_kg),
              },
              {
                label: 'Price',
                value: parcel.price != null && formatMoney(parcel.price, currency),
              },
            ].map((item) => (
              <div key={item.label} className="flex justify-between gap-3 sm:block">
                <dt className="text-muted-foreground">{item.label}</dt>
                <dd className="font-semibold">{item.value || '—'}</dd>
              </div>
            ))}
          </dl>
          {parcel.dangerous_confirmed && (
            <p className="text-muted-foreground">Charged at the dangerous goods rate.</p>
          )}
          {parcel.status === 'RECEIVED' && parcel.ship_week && (
            <p className="flex items-center gap-2 font-medium">
              <Plane className="h-4 w-4 shrink-0" aria-hidden />
              Flies the week of {formatShipWeek(parcel.ship_week)}
            </p>
          )}
        </div>
      )}

      {parcel.photo_ids.length > 0 && <ParcelPhotos parcel={parcel} />}

      {editable && <CancelParcel parcel={parcel} onChange={onChange} onStale={onStale} />}
    </li>
  )
}

function AddTrackingNumber({ parcel, onChange, onStale }: Omit<ParcelItemProps, 'currency'>) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputId = `tracking-${parcel.id}`

  const save = async (event: FormEvent) => {
    event.preventDefault()
    const trackingNumber = value.trim()
    if (!trackingNumber) {
      setError('Enter the tracking number the seller gave you.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      onChange(await apiClient.updateMyParcel(parcel.id, { tracking_number: trackingNumber }))
    } catch (cause) {
      setError(parcelErrorMessage(cause))
      if (cause instanceof ApiClientError && cause.code === 'PARCEL_NOT_EDITABLE') onStale()
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={save} noValidate className="mt-1 space-y-1 font-sans">
      <label htmlFor={inputId} className="sr-only">
        Tracking number for {parcel.description}
      </label>
      <div className="flex gap-2">
        <Input
          id={inputId}
          value={value}
          onChange={(event) => {
            setValue(event.target.value)
            setError(null)
          }}
          placeholder="Add tracking number"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={60}
          aria-invalid={Boolean(error)}
          className="h-10 min-w-0 flex-1"
        />
        <Button type="submit" variant="outline" className="h-10" disabled={busy}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
    </form>
  )
}

function CancelParcel({ parcel, onChange, onStale }: Omit<ParcelItemProps, 'currency'>) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cancel = async () => {
    setBusy(true)
    setError(null)
    try {
      onChange(await apiClient.updateMyParcel(parcel.id, { cancel: true }))
      setOpen(false)
    } catch (cause) {
      setError(parcelErrorMessage(cause))
      if (cause instanceof ApiClientError && cause.code === 'PARCEL_NOT_EDITABLE') onStale()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setError(null)
      }}
    >
      <Button
        variant="ghost"
        className="-ml-3 h-10 px-3 text-muted-foreground hover:text-destructive"
        onClick={() => setOpen(true)}
      >
        Cancel
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel this parcel?</DialogTitle>
          <DialogDescription>
            We&apos;ll stop expecting &ldquo;{parcel.description}&rdquo; at the warehouse.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="h-11 sm:h-9">
              Keep it
            </Button>
          </DialogClose>
          <Button variant="destructive" className="h-11 sm:h-9" onClick={cancel} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Cancel parcel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The warehouse's photos of a parcel; tap one to see it larger. */
function ParcelPhotos({ parcel }: { parcel: WarehouseParcel }) {
  const [enlarged, setEnlarged] = useState<{ url: string; index: number } | null>(null)
  const count = parcel.photo_ids.length

  return (
    <div>
      <p className="mb-2 text-sm text-muted-foreground">Photos from the warehouse</p>
      <ul className="flex flex-wrap gap-2">
        {parcel.photo_ids.map((photoId, index) => (
          <li key={photoId}>
            <PhotoThumb
              parcelId={parcel.id}
              photoId={photoId}
              label={`photo ${index + 1} of ${count}`}
              onOpen={(url) => setEnlarged({ url, index })}
            />
          </li>
        ))}
      </ul>

      <Dialog open={enlarged !== null} onOpenChange={(open) => !open && setEnlarged(null)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Photo {enlarged ? enlarged.index + 1 : ''} of {count}
            </DialogTitle>
            <DialogDescription className="break-words">{parcel.description}</DialogDescription>
          </DialogHeader>
          {enlarged && (
            <img
              src={enlarged.url}
              alt={`Photo ${enlarged.index + 1} of ${parcel.description}`}
              className="max-h-[65vh] w-full rounded-md bg-muted object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function PhotoThumb({
  parcelId,
  photoId,
  label,
  onOpen,
}: {
  parcelId: string
  photoId: string
  label: string
  onOpen: (url: string) => void
}) {
  const [photo, setPhoto] = useState<{ url: string | null; failed: boolean }>({
    url: null,
    failed: false,
  })

  useEffect(() => {
    let active = true
    let current: string | null = null
    apiClient.myParcelPhotoUrl(parcelId, photoId).then(
      (url) => {
        if (active) {
          current = url
          setPhoto({ url, failed: false })
        } else {
          URL.revokeObjectURL(url)
        }
      },
      () => {
        if (active) setPhoto({ url: null, failed: true })
      },
    )
    return () => {
      active = false
      if (current) URL.revokeObjectURL(current)
    }
  }, [parcelId, photoId])

  if (photo.failed) {
    return (
      <div
        role="img"
        aria-label={`The ${label} could not be loaded`}
        className="flex size-20 items-center justify-center rounded-md border bg-muted text-muted-foreground"
      >
        <ImageOff className="h-5 w-5" aria-hidden />
      </div>
    )
  }

  const url = photo.url
  if (!url) {
    return (
      <div
        className="size-20 animate-pulse rounded-md border bg-muted"
        aria-label="Loading photo"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(url)}
      aria-label={`Enlarge ${label}`}
      className="block size-20 overflow-hidden rounded-md border outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <img src={url} alt="" className="size-full object-cover" />
    </button>
  )
}
